import React, { useState, useMemo } from 'react';
import './styles.css';
import LayoutHome from '../../components/layout/LayoutHome';
import TableProductos from '../../components/productos/TableProductos';
import {
  Button, Divider, Input, Modal, Select, Typography, message,
  Grid, Drawer, Badge, Switch, Space, ConfigProvider
} from 'antd';
import {
  IoAdd, IoDownloadOutline, IoFilterOutline, IoSearchOutline, IoCreateOutline,
  IoCubeOutline, IoStorefrontOutline
} from "react-icons/io5";
import { DeleteOutlined, PrinterOutlined } from '@ant-design/icons';
import { useGetProductos, useSaveProducto, useDeleteProducto } from '../../hooks/useProductos';
import { useGetProveedores } from '../../hooks/useProveedores';
import { useGetBodegas, useGetStockBodega } from '../../hooks/useBodegas';
import { usePermission } from '../../hooks/usePermission';
import es_ES from 'antd/locale/es_ES';
import { useConfig } from '../../context/ConfigContext';
import ListasPrecios from './modals/ListasPrecios';
import FormularioProductos from './modals/FormularioProductos';
import ModalImprimirEtiquetas from './modals/ModalImprimirEtiquetas';
import ModalExportarProductos from './modals/ModalExportarProductos';
import CardProductoMobile from './components/CardProductoMobile';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const Productos = () => {

  const { hasPermission } = usePermission();
  const { config } = useConfig();

  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const [open, setOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [margenGanancia, setMargenGanancia] = useState("");
  const [filtroStock, setFiltroStock] = useState('todos');
  const [filtroEstado, setFiltroEstado] = useState('active');
  const [filtroDescuento, setFiltroDescuento] = useState('todos');
  const [filtroBodega, setFiltroBodega] = useState('global');
  const [filtroProveedor, setFiltroProveedor] = useState('todos'); // <-- NUEVO ESTADO DE PROVEEDOR
  const [drawerFiltros, setDrawerFiltros] = useState(false);

  // <-- ACTUALIZADO: Contador de filtros activos incluyendo proveedor
  const filtrosActivos = (filtroStock !== 'todos' ? 1 : 0) + (filtroEstado !== 'todos' ? 1 : 0) + (filtroDescuento !== 'todos' ? 1 : 0) + (filtroBodega !== 'global' ? 1 : 0) + (filtroProveedor !== 'todos' ? 1 : 0);

  const [isSavingPrecios, setIsSavingPrecios] = useState(false);
  const [openEtiquetas, setOpenEtiquetas] = useState(false);
  const [productosAImprimir, setProductosAImprimir] = useState([]);

  const [openExport, setOpenExport] = useState(false);
  const [exportConfig, setExportConfig] = useState({
    formato: 'excel',
    alcance: 'filtrados',
    columnas: ['nombre', 'codigo', 'precio_venta', 'stock', 'categoria']
  });

  const isPlanBasico = config?.plan === 'basico' || config?.plan_id === 1;

  const handleImprimirEtiqueta = (prod) => {
    if (!prod.has_variants) {
      setProductosAImprimir([prod]);
    } else {
      const variantesAImprimir = prod.variantes.map(variante => {
        const nombreCompleto = `${prod.nombre} - ${variante.atributos?.nombre || 'Variante'}`;
        return {
          id: variante.id || `${prod.id}-${variante.codigo_barras || variante.codigo_interno || Math.random()}`,
          nombre: nombreCompleto,
          codigo_barras: variante.codigo_barras,
          codigo_interno: variante.codigo_interno,
          codigoBarras: variante.codigo_barras,
          codigoInterno: variante.codigo_interno
        };
      });
      setProductosAImprimir(variantesAImprimir);
    }
    setOpenEtiquetas(true);
  };

  const [producto, setProducto] = useState({
    id: null, codigoBarras: "", codigoInterno: "", nombre: "", descripcion: "", categoria: "",
    precioCompra: "", precioVenta: "", impuesto: 0, stock: 0, unidadMedida: "", presentacion: "",
    marca: "", proveedor_id: null, ubicacion: "", status: "active", image: "", descuento: 0,
    preciosAdicionales: {}, has_variants: false, variantes: [],
  });

  const { data: proveedoresDB = [] } = useGetProveedores();
  const { data: productosDB = [], isLoading } = useGetProductos();
  const { data: bodegas = [] } = useGetBodegas();
  const { data: stockBodegasDB = [] } = useGetStockBodega(filtroBodega !== 'global' ? filtroBodega : null);

  const saveMutation = useSaveProducto();
  const deleteMutation = useDeleteProducto();

  const calcularPrecioDesdeMargen = (costo, porcentaje) => {
    const c = Number(costo) || 0; const p = Number(porcentaje) || 0;
    if (c === 0 || p >= 100) return "";
    const precio = c / (1 - (p / 100));
    return Math.round(precio).toString();
  };

  const calcularMargenDesdePrecio = (costo, precioVenta) => {
    const c = Number(costo) || 0; const v = Number(precioVenta) || 0;
    if (c === 0 || v === 0 || v <= c) return "";
    const porcentaje = ((v - c) / v) * 100;
    return Number(porcentaje.toFixed(1)).toString();
  };

  // --- LÓGICA DE BODEGAS (Filtra y asigna stock real) ---
  // --- LÓGICA DE BODEGAS (Reglas estrictas de negocio) ---
  const productosConStockReal = useMemo(() => {
    // 1. Vista Global: Muestra el catálogo intacto de la BD principal
    if (filtroBodega === 'global') return productosDB;

    const bodegaActual = bodegas.find(b => String(b.id) === String(filtroBodega));
    const esPrincipal = bodegaActual?.es_default === true;

    const resultado = [];

    productosDB.forEach(p => {
      let incluirProducto = false;
      let pActualizado = { ...p };

      // ==========================================
      // A. LÓGICA DE COMBOS
      // ==========================================
      if (p.es_combo) {
        if (esPrincipal) {
          incluirProducto = true; // En la principal siempre se ven los combos
        } else {
          // En secundarias, solo aparece si al menos UNO de sus ingredientes está en esa bodega
          const tieneIngredientesEnBodega = p.combo_items?.some(item =>
            stockBodegasDB.some(sb => sb.producto_id === item.producto_id && (sb.variante_id === item.variante_id || (!item.variante_id && !sb.variante_id)))
          );
          if (tieneIngredientesEnBodega) incluirProducto = true;
        }
        pActualizado.stock = 'Dinámico';
      }

      // ==========================================
      // B. LÓGICA PRODUCTOS SIMPLES
      // ==========================================
      else if (!p.has_variants) {
        // En lugar de buscar un solo registro, filtramos todos los que coincidan y los sumamos
        const registrosStock = stockBodegasDB.filter(sb => sb.producto_id === p.id && !sb.variante_id);

        if (registrosStock.length > 0) {
          incluirProducto = true;
          // Sumamos todos los registros duplicados si existen
          pActualizado.stock = registrosStock.reduce((total, actual) => total + Number(actual.stock), 0);
        } else if (esPrincipal) {
          incluirProducto = true;
          pActualizado.stock = Number(p.stock) || 0;
        }
      }

      // ==========================================
      // C. LÓGICA PRODUCTOS CON VARIANTES
      // ==========================================
      else {
        let algunaVarianteEnBodega = false;

        pActualizado.variantes = p.variantes.map(v => {
          const stockVarFound = stockBodegasDB.find(sb => sb.producto_id === p.id && sb.variante_id === v.id);
          if (stockVarFound) {
            algunaVarianteEnBodega = true;
            return { ...v, stock: Number(stockVarFound.stock) };
          }
          // FIX VARIANTES NUEVAS: Hereda stock general si estamos en la principal
          return { ...v, stock: esPrincipal ? (Number(v.stock) || 0) : 0 };
        });

        if (algunaVarianteEnBodega || esPrincipal) {
          incluirProducto = true;
          pActualizado.stock = pActualizado.variantes.reduce((sum, v) => sum + v.stock, 0);
        }
      }

      // Si el producto pasó los filtros, lo agregamos al resultado
      if (incluirProducto) {
        resultado.push(pActualizado);
      }
    });

    return resultado;
  }, [productosDB, stockBodegasDB, filtroBodega, bodegas]);

  // --- KPIs Dinámicos ---
  const metricasOperativas = useMemo(() => {
    const total = productosConStockReal.length;
    const bajoStock = productosConStockReal.filter(p => !p.es_combo && Number(p.stock) > 0 && Number(p.stock) <= 5).length;
    const agotados = productosConStockReal.filter(p => !p.es_combo && Number(p.stock) <= 0).length;
    const enOferta = productosConStockReal.filter(p => Number(p.descuento) > 0).length;

    return { total, bajoStock, agotados, enOferta };
  }, [productosConStockReal]);

  // --- FILTROS Y BÚSQUEDA ---
  const productosFiltrados = useMemo(() => {
    let resultado = productosConStockReal;

    if (busqueda.trim() !== '') {
      const lowerBusqueda = busqueda.toLowerCase();
      resultado = resultado.filter(p => {
        const matchPadre = p.nombre?.toLowerCase().includes(lowerBusqueda) ||
          p.categoria?.toLowerCase().includes(lowerBusqueda) ||
          p.codigo_barras?.includes(busqueda) ||
          p.codigo_interno?.includes(busqueda);

        const matchVariante = p.has_variants && p.variantes?.some(v =>
          v.codigo_barras?.includes(busqueda) ||
          v.codigo_interno?.includes(busqueda) ||
          v.nombre?.toLowerCase().includes(lowerBusqueda)
        );
        return matchPadre || matchVariante;
      });
    }

    if (filtroEstado !== 'todos') resultado = resultado.filter(p => p.status === filtroEstado);

    if (filtroStock === 'agotados') resultado = resultado.filter(p => Number(p.stock) <= 0);
    else if (filtroStock === 'bajo_stock') resultado = resultado.filter(p => Number(p.stock) > 0 && Number(p.stock) <= 5);
    else if (filtroStock === 'con_stock') resultado = resultado.filter(p => Number(p.stock) > 0);
    else if (filtroStock === 'con_variantes') resultado = resultado.filter(p => p.has_variants === true);

    if (filtroDescuento === 'con_descuento') resultado = resultado.filter(p => Number(p.descuento) > 0);
    else if (filtroDescuento === 'sin_descuento') resultado = resultado.filter(p => Number(p.descuento) === 0 || !p.descuento);

    // <-- NUEVO: Filtro por proveedor ID
    if (filtroProveedor !== 'todos') {
      resultado = resultado.filter(p => String(p.proveedor_id) === String(filtroProveedor));
    }

    const formatoCOP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 });

    return resultado.map(p => ({
      ...p, codigoBarras: p.codigo_barras,
      precioCompra: formatoCOP.format(p.precio_compra || 0),
      precioVenta: formatoCOP.format(p.precio_venta || 0),
    }));

  }, [productosConStockReal, busqueda, filtroEstado, filtroStock, filtroDescuento, filtroProveedor]);

  const [openPrecios, setOpenPrecios] = useState(false);
  const [targetPrecios, setTargetPrecios] = useState(null);
  const [tempPrecios, setTempPrecios] = useState({});

  const listasPrecios = useMemo(() => {
    const tarifas = config?.config_tarifas || {};
    return Object.entries(tarifas).filter(([_, data]) => data.activa).map(([key, data]) => ({ id: key, nombre: data.nombre }));
  }, [config]);

  const handlePrepararEdicion = (productoEditar) => {
    const varDefecto = productoEditar.variantes?.[0] || {};
    const preciosAdicPadre = { lista_1: varDefecto.precio_lista_1 || "", lista_2: varDefecto.precio_lista_2 || "", lista_3: varDefecto.precio_lista_3 || "" };

    setProducto({
      id: productoEditar.id, codigoBarras: productoEditar.codigo_barras, codigoInterno: productoEditar.codigo_interno || "",
      nombre: productoEditar.nombre, descripcion: productoEditar.descripcion, categoria: productoEditar.categoria,
      precioCompra: productoEditar.precio_compra, precioVenta: productoEditar.precio_venta,
      precioCatalogo: productoEditar.precio_catalogo || productoEditar.precio_venta,
      preciosAdicionales: productoEditar.has_variants ? {} : preciosAdicPadre,
      impuesto: productoEditar.iva, stock: productoEditar.stock, unidadMedida: productoEditar.unidad_medida,
      presentacion: productoEditar.presentacion, marca: productoEditar.marca, proveedor_id: productoEditar.proveedor_id || null,
      ubicacion: productoEditar.ubicacion, status: productoEditar.status, image: productoEditar.imagen_url,
      descuento: productoEditar.descuento, visibleCatalogo: productoEditar.visible_catalogo !== false,
      has_variants: productoEditar.has_variants || false, es_combo: productoEditar.es_combo || false, comboItems: productoEditar.combo_items || [],
      variantes: productoEditar.variantes && productoEditar.variantes.length > 0
        ? productoEditar.variantes.map(v => ({ ...v, codigo_barras: v.codigo_barras || "", codigo_interno: v.codigo_interno || "", precios_adicionales: { lista_1: v.precio_lista_1 || "", lista_2: v.precio_lista_2 || "", lista_3: v.precio_lista_3 || "" } }))
        : []
    });

    const margen = calcularMargenDesdePrecio(productoEditar.precio_compra, productoEditar.precio_venta);
    setMargenGanancia(margen);
    setOpen(true);
  };

  const estadoInicialProducto = {
    id: null, codigoBarras: "", codigoInterno: "", nombre: "", descripcion: "", categoria: "", precioCompra: "", precioVenta: "", precioCatalogo: "",
    impuesto: 0, stock: 0, unidadMedida: "", presentacion: "", marca: "", proveedor_id: null, ubicacion: "", status: "active", image: "",
    descuento: 0, visibleCatalogo: true, has_variants: false, variantes: [], preciosAdicionales: {}, es_combo: false, comboItems: [],
  };

  const handleStatusChange = async (productoActualizado) => {
    try { await saveMutation.mutateAsync({ ...productoActualizado, id: productoActualizado.id }); }
    catch (error) { console.error("Error al cambiar estado:", error); }
  };

  const handleEliminarProducto = (prod) => {
    Modal.confirm({
      title: <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#ff4d4f' }}>¿Eliminar producto definitivamente?</span>,
      content: `Estás a punto de eliminar "${prod.nombre}". Esta acción quitará el producto de tu inventario y catálogos de forma permanente, pero conservará su historial contable. ¿Deseas continuar?`,
      okText: 'Sí, eliminar', cancelText: 'Cancelar', centered: true,
      okButtonProps: { danger: true, style: { height: '40px', borderRadius: '8px', fontWeight: 'bold' } },
      cancelButtonProps: { style: { height: '40px', borderRadius: '8px', fontWeight: '600' } },
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(prod.id);
          message.success("Producto enviado a la papelera.");
        } catch (error) { message.error("No se pudo eliminar el producto."); }
      }
    });
  };

  const guardarPreciosAdicionales = () => {
    const tieneCamposVacios = Object.values(tempPrecios).some(val => val === "" || val === undefined || Number(val) <= 0);
    if (tieneCamposVacios) { message.warning("Por favor, asigna un precio válido (mayor a 0) en todas las listas de precios activas."); return; }
    if (isSavingPrecios) return;
    setIsSavingPrecios(true);
    if (targetPrecios === 'simple') { setProducto({ ...producto, preciosAdicionales: tempPrecios }); }
    else { const nuevasVariantes = [...producto.variantes]; nuevasVariantes[targetPrecios].precios_adicionales = tempPrecios; setProducto({ ...producto, variantes: nuevasVariantes }); }
    setOpenPrecios(false); setIsSavingPrecios(false);
  };

  return (
    <ConfigProvider locale={es_ES}>
      <LayoutHome pageSeleccionada="productos">
        <style>{`
        /* Mejoras en la tabla */
        .tabla-productos-compacta .ant-table-tbody > tr > td { padding: 4px 12px !important; height: 32px !important; line-height: 1.2 !important; font-size: 13px; }
        .tabla-productos-compacta .ant-table-thead > tr > th { padding: 10px 12px !important; background: #f8fafc !important; color: #475569 !important; font-weight: 600 !important; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0; }
        
        /* Modal Fixes */
        .modal-yugonub label { font-weight: 600; color: #475569; margin-bottom: 3px; display: inline-block; font-size: 14px; }
        .modal-yugonub .ant-input, .modal-yugonub .ant-input-number-input { font-size: 17px !important; font-weight: 600 !important; }
        
        /* Selector de Bodega Compacto */
        .bodega-select-compact .ant-select-selector { border: none !important; background: transparent !important; box-shadow: none !important; font-weight: 700 !important; color: #0f172a !important; padding: 0 4px !important; }
        
        /* Barra de herramientas / inputs */
        .toolbar-input { border-radius: 8px !important; border-color: #cbd5e1 !important; box-shadow: none !important; }
        .toolbar-input:focus-within, .toolbar-select .ant-select-selector:focus { border-color: #22BABB !important; }
        .toolbar-select .ant-select-selector { border-radius: 8px !important; border-color: #cbd5e1 !important; }

        /* Contenedor Fijo para Paginación */
        .contenedor-tabla-fija { display: flex; flex-direction: column; height: 100%; width: 100%; }
        .contenedor-tabla-fija .ant-table-wrapper { display: flex; flex-direction: column; flex: 1; min-height: 0; }
        .contenedor-tabla-fija .ant-spin-nested-loading { display: flex; flex-direction: column; flex: 1; min-height: 0; }
        .contenedor-tabla-fija .ant-spin-container { display: flex; flex-direction: column; flex: 1; min-height: 0; }
        .contenedor-tabla-fija .ant-table { display: flex; flex-direction: column; flex: 1; min-height: 0; }
        .contenedor-tabla-fija .ant-table-container { display: flex; flex-direction: column; flex: 1; min-height: 0; }
        .contenedor-tabla-fija .ant-table-body { flex: 1; overflow-y: auto !important; }
        .contenedor-tabla-fija .ant-pagination { margin-top: auto !important; padding: 12px 16px !important; border-top: 1px solid #e2e8f0; background: #fff; margin-bottom: 0 !important; }
      `}</style>

        <FormularioProductos productosDB={productosDB} producto={producto} saveMutation={saveMutation} setOpen={setOpen} open={open} setProducto={setProducto} estadoInicialProducto={estadoInicialProducto} setMargenGanancia={setMargenGanancia} calcularMargenDesdePrecio={calcularMargenDesdePrecio} margenGanancia={margenGanancia} calcularPrecioDesdeMargen={calcularPrecioDesdeMargen} setTargetPrecios={setTargetPrecios} listasPrecios={listasPrecios} setTempPrecios={setTempPrecios} setOpenPrecios={setOpenPrecios} proveedoresDB={proveedoresDB} />
        <ModalImprimirEtiquetas visible={openEtiquetas} onClose={() => setOpenEtiquetas(false)} productos={productosAImprimir} />
        <ListasPrecios setOpenPrecios={setOpenPrecios} openPrecios={openPrecios} isSavingPrecios={isSavingPrecios} guardarPreciosAdicionales={guardarPreciosAdicionales} listasPrecios={listasPrecios} targetPrecios={targetPrecios} producto={producto} tempPrecios={tempPrecios} setTempPrecios={setTempPrecios} calcularPrecioDesdeMargen={calcularPrecioDesdeMargen} calcularMargenDesdePrecio={calcularMargenDesdePrecio} />

        {/* CONTENEDOR PRINCIPAL FIJO */}
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden', background: '#f8fafc', padding: isMobile ? '10px' : '15px' }}>

          {/* 1. HEADER ULTRA COMPACTO */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: '12px', marginBottom: '15px', flexShrink: 0, background: '#fff', padding: '12px 16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>

            {/* Bloque Izquierdo: Título y Selector de Bodega */}
            <div style={{ display: 'flex', justifyContent: isMobile ? 'space-between' : 'flex-start', alignItems: 'center', gap: '12px' }}>
              <Title level={4} style={{ margin: 0, color: '#0f172a', whiteSpace: 'nowrap' }}>Productos</Title>
              {!isMobile && <Divider type="vertical" style={{ height: '24px', margin: 0 }} />}

              <div style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', padding: '4px 10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <IoStorefrontOutline size={16} color="#64748b" style={{ marginRight: '6px' }} />
                <Select
                  value={filtroBodega}
                  onChange={(val) => {
                    if (val === 'upgrade') {
                      message.info("Redirigiendo a los planes premium...");
                      return;
                    }
                    setFiltroBodega(val);
                  }}
                  className="bodega-select-compact"
                  style={{ width: isMobile ? 140 : 190 }}
                  dropdownMatchSelectWidth={false}
                >
                  <Select.Option value="global">Todas las Bodegas</Select.Option>
                  {isPlanBasico ? (
                    <Select.Option value="upgrade" style={{ background: '#fffbeb', borderTop: '1px solid #fef3c7' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#d97706', fontWeight: '700' }}>
                        👑 Multi-Bodega
                      </div>
                    </Select.Option>
                  ) : (
                    bodegas.filter(b => b.activa).map(b => (
                      <Select.Option key={b.id} value={b.id}>{b.nombre}</Select.Option>
                    ))
                  )}
                </Select>
              </div>
            </div>

            {/* Bloque Central: KPIs Minimalistas (SOLO LECTURA EN PC) */}
            {!isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.2' }}>
                  <Text type="secondary" style={{ fontSize: '10px', fontWeight: '700' }}>TOTAL PROD.</Text>
                  <Text strong style={{ fontSize: '15px' }}>{metricasOperativas.total}</Text>
                </div>
                <Divider type="vertical" style={{ height: '24px', margin: 0 }} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.2' }}>
                  <Text type="secondary" style={{ fontSize: '10px', fontWeight: '700', color: '#ef4444' }}>STOCK BAJO</Text>
                  <Text strong style={{ fontSize: '15px', color: '#ef4444' }}>{metricasOperativas.bajoStock}</Text>
                </div>
                <Divider type="vertical" style={{ height: '24px', margin: 0 }} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.2' }}>
                  <Text type="secondary" style={{ fontSize: '10px', fontWeight: '700' }}>AGOTADOS</Text>
                  <Text strong style={{ fontSize: '15px' }}>{metricasOperativas.agotados}</Text>
                </div>
                <Divider type="vertical" style={{ height: '24px', margin: 0 }} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.2' }}>
                  <Text type="secondary" style={{ fontSize: '10px', fontWeight: '700', color: '#10b981' }}>PROD. EN OFERTAS</Text>
                  <Text strong style={{ fontSize: '15px', color: '#10b981' }}>{metricasOperativas.enOferta}</Text>
                </div>
              </div>
            )}

            {/* Bloque Derecho: Botones (Lado a lado en móvil) */}
            <div style={{ display: 'flex', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
              <Button
                onClick={() => setOpenExport(true)}
                icon={<IoDownloadOutline size={18} />}
                style={{ flex: isMobile ? 1 : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '38px', borderRadius: '8px', borderColor: '#cbd5e1', color: '#475569', fontWeight: '600' }}
              >
                Exportar
              </Button>
              <Button
                disabled={!hasPermission('productos:crear')}
                type="primary"
                icon={<IoAdd size={18} />}
                style={{ flex: isMobile ? 1 : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '38px', borderRadius: '8px', backgroundColor: '#22BABB', fontWeight: '600', border: 'none' }}
                onClick={() => { setProducto(estadoInicialProducto); setMargenGanancia(""); setOpen(true); }}
              >
                Nuevo
              </Button>
            </div>
          </div>

          {isMobile && (
            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', marginBottom: '12px', paddingBottom: '4px', flexShrink: 0 }} className="scroll-personalizado">
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '4px 12px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                <Text style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>TOTAL:</Text>
                <Text strong style={{ fontSize: '12px', color: '#0f172a' }}>{metricasOperativas.total}</Text>
              </div>
              <div style={{ background: '#fefce8', border: '1px solid #fef08a', padding: '4px 12px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                <Text style={{ fontSize: '11px', fontWeight: 700, color: '#ca8a04' }}>BAJO STOCK:</Text>
                <Text strong style={{ fontSize: '12px', color: '#ca8a04' }}>{metricasOperativas.bajoStock}</Text>
              </div>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '4px 12px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                <Text style={{ fontSize: '11px', fontWeight: 700, color: '#ef4444' }}>AGOTADOS:</Text>
                <Text strong style={{ fontSize: '12px', color: '#ef4444' }}>{metricasOperativas.agotados}</Text>
              </div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '4px 12px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                <Text style={{ fontSize: '11px', fontWeight: 700, color: '#16a34a' }}>OFERTAS:</Text>
                <Text strong style={{ fontSize: '12px', color: '#16a34a' }}>{metricasOperativas.enOferta}</Text>
              </div>
            </div>
          )}

          {/* 2. BARRA DE FILTROS SECUNDARIA (Incluyendo Proveedor) */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexShrink: 0 }}>
            <Input
              className="toolbar-input"
              placeholder={isMobile ? "Buscar nombre o código..." : "Buscar por nombre, código de barras o categoría..."}
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              allowClear
              prefix={<IoSearchOutline color="#94a3b8" size={18} style={{ marginRight: '6px' }} />}
              style={{ flex: 1, height: '38px' }}
            />

            {!isMobile && (
              <>
                <Select
                  className="toolbar-select"
                  showSearch
                  placeholder="Proveedor"
                  value={filtroProveedor}
                  onChange={setFiltroProveedor}
                  style={{ width: 170, height: '38px' }}
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={[
                    { value: 'todos', label: 'Todos los proveedores' },
                    ...proveedoresDB.map(p => ({ value: p.id, label: p.nombre }))
                  ]}
                />
                <Select
                  className="toolbar-select"
                  value={filtroStock}
                  onChange={setFiltroStock}
                  style={{ width: 150, height: '38px' }}
                  options={[{ value: 'todos', label: 'Cualquier Stock' }, { value: 'con_stock', label: 'En Inventario' }, { value: 'bajo_stock', label: 'Bajo Stock' }, { value: 'agotados', label: 'Agotados' }, { value: 'con_variantes', label: 'Con Variantes' }]}
                />
                <Select
                  className="toolbar-select"
                  value={filtroEstado}
                  onChange={setFiltroEstado}
                  style={{ width: 130, height: '38px' }}
                  options={[{ value: 'todos', label: 'Todos los estados' }, { value: 'active', label: 'Solo Activos' }, { value: 'inactive', label: 'Inactivos' }]}
                />
                <Select
                  className="toolbar-select"
                  value={filtroDescuento}
                  onChange={setFiltroDescuento}
                  style={{ width: 130, height: '38px' }}
                  options={[{ value: 'todos', label: 'Cualquier Precio' }, { value: 'con_descuento', label: 'En Oferta' }, { value: 'sin_descuento', label: 'Normal' }]}
                />
              </>
            )}

            {isMobile && (
              <Badge count={filtrosActivos} size="small">
                <Button icon={<IoFilterOutline size={20} />} style={{ height: '38px', width: '38px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', border: '1px solid #cbd5e1' }} onClick={() => setDrawerFiltros(true)} />
              </Badge>
            )}
          </div>

          {/* 3. ZONA DE TABLA FIJA */}
          <div style={{ flex: 1, minHeight: 0, background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            {isMobile ? (
              <div style={{ height: '100%', overflowY: 'auto', padding: '12px', background: '#f8fafc' }} className="scroll-personalizado">
                {productosFiltrados.map(prod => (
                  <CardProductoMobile
                    key={prod.id}
                    prod={prod}
                    hasPermission={hasPermission}
                    onPrint={handleImprimirEtiqueta}
                    onEdit={handlePrepararEdicion}
                    onDelete={handleEliminarProducto}
                    onStatusChange={handleStatusChange}
                  />
                ))}
                {productosFiltrados.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                    <IoCubeOutline size={40} style={{ opacity: 0.5, marginBottom: '10px' }} />
                    <p>No se encontraron productos</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="contenedor-tabla-fija tabla-productos-compacta">
                <TableProductos
                  dataProductos={productosFiltrados}
                  onEdit={handlePrepararEdicion}
                  onDelete={handleEliminarProducto}
                  onPrint={handleImprimirEtiqueta}
                  proveedoresDB={proveedoresDB}
                  onStatusChange={handleStatusChange}
                  loading={isLoading}
                  rowKey="id"
                  scroll={{ x: 1500, y: 'calc(100vh - 290px)' }}
                />
              </div>
            )}
          </div>
        </div>

        {/* --- EXTRAS MÓVIL --- */}
        {isMobile && (
          <Drawer title={<span style={{ fontWeight: '700' }}>Filtros de Búsqueda</span>} placement="bottom" onClose={() => setDrawerFiltros(false)} open={drawerFiltros} height="auto" styles={{ body: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' } }}>
            <div>
              <Text type="secondary" style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Proveedor</Text>
              <Select
                showSearch
                style={{ width: '100%', height: '45px', marginTop: '5px' }}
                placeholder="Todos los proveedores"
                value={filtroProveedor}
                onChange={setFiltroProveedor}
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: 'todos', label: 'Todos los proveedores' }, ...proveedoresDB.map(p => ({ value: p.id, label: p.nombre }))]}
              />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Nivel de Inventario</Text>
              <Select value={filtroStock} onChange={setFiltroStock} style={{ width: '100%', height: '45px', marginTop: '5px' }} options={[{ value: 'todos', label: 'Cualquier Stock' }, { value: 'con_stock', label: 'En Inventario' }, { value: 'bajo_stock', label: 'Bajo Stock (≤ 5)' }, { value: 'agotados', label: 'Agotados (0)' }, { value: 'con_variantes', label: 'Con Variantes' }]} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Estado del Producto</Text>
              <Select value={filtroEstado} onChange={setFiltroEstado} style={{ width: '100%', height: '45px', marginTop: '5px' }} options={[{ value: 'active', label: 'Activos' }, { value: 'inactive', label: 'Inactivos' }, { value: 'todos', label: 'Todos los estados' }]} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Promociones</Text>
              <Select value={filtroDescuento} onChange={setFiltroDescuento} style={{ width: '100%', height: '45px', marginTop: '5px' }} options={[{ value: 'todos', label: 'Todos' }, { value: 'con_descuento', label: 'En Oferta' }, { value: 'sin_descuento', label: 'Precio Normal' }]} />
            </div>
          </Drawer>
        )}

        <ModalExportarProductos
          openExport={openExport}
          setOpenExport={setOpenExport}
          exportConfig={exportConfig}
          setExportConfig={setExportConfig}
          productosFiltrados={productosFiltrados}
          productosDB={productosDB}
        />
      </LayoutHome>
    </ConfigProvider>
  )
}

export default Productos;